/**
 * Chamadas HTTP do histórico de manutenção.
 *
 * Papel: aqui ficam as quatro chamadas de `/api/manutencoes-veiculo` que a tela
 * usa — listar, criar, alterar e excluir uma manutenção.
 *
 * Como usar: a listagem devolve o array inteiro, da manutenção mais recente
 * para a mais antiga, com o veículo aninhado. Placa e modelo vêm de lá, e não
 * da própria manutenção.
 */
import {
  type ManutencaoVeiculo,
  type NovaManutencaoVeiculo,
  manutencaoVeiculoSchema,
} from '@concierge/shared';
import { z } from 'zod';

import { clienteApi } from '@/shared/api';

/** A listagem inteira, sem envelope de paginação. */
const listaDeManutencoesSchema = z.array(manutencaoVeiculoSchema);

/**
 * Lê todas as manutenções, da mais recente para a mais antiga.
 *
 * @param sinal - Sinal de cancelamento repassado pelo TanStack Query.
 * @returns O histórico, cada linha com o veículo aninhado.
 */
export function buscarManutencoes(sinal?: AbortSignal): Promise<ManutencaoVeiculo[]> {
  return clienteApi.get('/manutencoes-veiculo', { schema: listaDeManutencoesSchema, sinal });
}

/**
 * Cria uma manutenção.
 *
 * A API responde 409 quando o veículo escolhido deixou de existir enquanto o
 * formulário estava aberto.
 *
 * @param dados - A manutenção preenchida, já validada.
 * @returns A manutenção como ficou gravada, com o veículo aninhado.
 */
export function criarManutencao(dados: NovaManutencaoVeiculo): Promise<ManutencaoVeiculo> {
  return clienteApi.post('/manutencoes-veiculo', dados, { schema: manutencaoVeiculoSchema });
}

/**
 * Substitui todos os campos de uma manutenção.
 *
 * @param id - Identificador da linha em `manutencoes_veiculo`.
 * @param dados - A manutenção inteira, e não apenas os campos alterados.
 * @returns A manutenção como ficou gravada.
 */
export function atualizarManutencao(
  id: string,
  dados: NovaManutencaoVeiculo,
): Promise<ManutencaoVeiculo> {
  return clienteApi.put(`/manutencoes-veiculo/${encodeURIComponent(id)}`, dados, {
    schema: manutencaoVeiculoSchema,
  });
}

/**
 * Apaga uma manutenção.
 *
 * Não recebe schema porque a API responde 204 sem corpo.
 *
 * @param id - Identificador da linha em `manutencoes_veiculo`.
 */
export function excluirManutencao(id: string): Promise<void> {
  return clienteApi.remover(`/manutencoes-veiculo/${encodeURIComponent(id)}`);
}
